<template>
	<div class="modal-overlay">
		<div class="modal-content">
			<div class="modal-header">
				<h3>Courtesy Re-pull & Re-decision</h3>
				<span class="close" @click="handleClose" :class="{ disabled: isSubmitting }">&times;</span>
			</div>

			<div class="modal-body">
				<p>
					Before performing a courtesy re-pull, please confirm that you have received consent for the
					borrower(s) on this LOC. Check below if consent was received.
				</p>
				<form>
					<div v-if="isLoading" class="spinner-container">
						<Spinner />
					</div>
					<div v-else class="form-container">
						<div>
							<div class="form-input">
								<label for="borrowerConsent" class="checkbox-label">
									<input
										type="checkbox"
										id="borrowerConsent"
										name="borrowerConsent"
										v-model="borrowerConsent"
										:disabled="isCheckboxDisabled"
										required />
									<span class="custom-checkbox"></span>
									<span>
										{{ primaryBorrower ? primaryBorrower.fullName : 'Primary Borrower' }}
										<span class="required">*</span>
									</span>
								</label>
							</div>
							<div class="form-input">
								<label for="coBorrowerConsent" class="checkbox-label">
									<input
										type="checkbox"
										id="coBorrowerConsent"
										name="coBorrowerConsent"
										v-model="coBorrowerConsent"
										:disabled="isCheckboxDisabled || isCoBorrowerDisabled"
										required />
									<span class="custom-checkbox"></span>
									<span>
										{{ coBorrower ? coBorrower.fullName : 'Co-borrower (N/A)' }}
										<span class="required">*</span>
									</span>
								</label>
							</div>
						</div>
						<div class="form-input select">
							<label>
								For which borrower(s) do you want to perform the soft credit re-pull?
								<span class="required">*</span>
							</label>
							<select v-model="selectedBorrowers" :disabled="isLoading || isSubmitting" required>
								<option key="none" value="" disabled selected>Select an option</option>
								<option v-for="option in borrowerOptions" :key="option.key" :value="option.value">
									{{ option.label }}
								</option>
							</select>
						</div>
						<div v-if="isCUApprovalRequired" class="form-input">
							<label for="cuApproved" class="checkbox-label">
								<input
									type="checkbox"
									id="cuApproved"
									name="cuApproved"
									v-model="cuApproved"
									:disabled="isCheckboxDisabled"
									required />
								<span class="custom-checkbox"></span>
								<span>
									CU approved override if after September 30<sup>th</sup>
									<span class="required"> *</span>
								</span>
							</label>
						</div>
					</div>
				</form>
				<p>
					By clicking the <i>“Re-Pull & Re-Decision”</i> button, I am kicking off the process to re-pull
					credit and re-decision this line of credit for annual review.
				</p>
				<div v-if="errorProcessing" class="error-container">
					<ExclamationCircleIcon class="error-icon" />
					<p class="error">
						The re-pull and re-decision failed to process. Please close this and try again later.
					</p>
				</div>
			</div>

			<div class="modal-footer">
				<Spinner v-if="isSubmitting" class="small" />
				<button class="secondary" @click="handleClose" :disabled="isSubmitting">Cancel</button>
				<button @click="submitRepullAsync" :disabled="!isFormValid || isSubmitting">
					Re-pull & Re-decision
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, onUnmounted, ref, computed } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import Spinner from '@/components/shared/Spinner.vue';
	import { LoanBorrower } from '@/models/loans';
	import { ExclamationCircleIcon } from '@heroicons/vue/24/solid';

	const props = defineProps<{
		annualReviewId?: number;
		annualReviewYear?: number;
		loanId: number;
		onClose: () => void;
	}>();

	const isLoading = ref<boolean>(false);
	const isSubmitting = ref<boolean>(false);
	const errorProcessing = ref<boolean>(false);

	const borrowerConsent = ref<boolean>(false);
	const coBorrowerConsent = ref<boolean>(false);
	const selectedBorrowers = ref<number | null>(null);
	const cuApproved = ref<boolean>(false);

	const borrowers = ref<LoanBorrower[]>([]);
	const primaryBorrower = ref<LoanBorrower>();
	const coBorrower = ref<LoanBorrower | null>(null);

	const isCUApprovalRequired = computed(() => {
		if (!props.annualReviewYear) {
			return false;
		}

		const today = new Date();
		const cutoffDate = new Date(props.annualReviewYear, 8, 30);

		return today > cutoffDate; // required if after sept 30 for the ar year
	});

	const isFormValid = computed(() => {
		const hasBorrowerConsent = borrowerConsent.value;
		const hasCoBorrowerConsent = coBorrower.value ? coBorrowerConsent.value : true;
		const hasSelectedBorrower = selectedBorrowers.value !== null;
		const isCUApprovedValid = isCUApprovalRequired.value ? cuApproved.value : true;

		return hasBorrowerConsent && hasCoBorrowerConsent && hasSelectedBorrower && isCUApprovedValid;
	});

	const isCheckboxDisabled = computed(() => isLoading.value || isSubmitting.value);
	const isCoBorrowerDisabled = computed(() => coBorrower.value === null);

	const borrowerOptions = computed(() => {
		if (!coBorrower.value) {
			return [{ key: 'primary', value: 0, label: primaryBorrower.value?.fullName }];
		}

		return [
			{ key: 'primary', value: 0, label: primaryBorrower.value?.fullName },
			{ key: 'co', value: 1, label: coBorrower.value?.fullName },
			{ key: 'both', value: 2, label: 'Both' }
		];
	});

	function handleClose() {
		if (isSubmitting.value) {
			return;
		}

		props.onClose();
	}

	async function submitRepullAsync() {
		isSubmitting.value = true;
		errorProcessing.value = false;

		try {
			const queryParams = {
				loanId: props.loanId
			};

			const selectedBorrowersList = borrowers.value.filter((b) => {
				switch (selectedBorrowers.value) {
					case 0:
						return b.borrowerTypeId === 1;
					case 1:
						return b.borrowerTypeId === 2;
					case 2:
						return true;
					default:
						return false;
				}
			});

			const request = selectedBorrowersList.map((b) => ({
				borrowerId: b.id,
				firstName: b.firstName,
				lastName: b.lastName
			}));

			await axios.post(`/api/annualreview/${props.annualReviewId}/repull`, request, { params: queryParams });

			functions.openModal({
				title: 'Success!',
				description: `Courtesy re-pull & re-decision successfully queued for borrower(s).`
			});

			props.onClose();
		} catch {
			errorProcessing.value = true;
		} finally {
			isSubmitting.value = false;
		}
	}

	async function fetchRepullDetailsAsync() {
		isLoading.value = true;

		try {
			const response = await axios.get<LoanBorrower[]>(`/api/loanservice/loans/${props.loanId}/borrowers`);

			borrowers.value = response.data.map(
				(b) => new LoanBorrower(b.id, b.borrowerTypeId, b.firstName, b.lastName)
			);

			primaryBorrower.value = borrowers.value.find((b) => b.borrowerTypeId === 1);
			coBorrower.value = borrowers.value.find((b) => b.borrowerTypeId === 2) || null;
		} catch {
			functions.openModal({
				title: 'Oops!',
				description: `There was an issue getting the re-pull and re-decision details. Please try again later.`
			});
		} finally {
			isLoading.value = false;
		}
	}

	onMounted(async () => {
		document.body.style.overflow = 'hidden';

		await fetchRepullDetailsAsync();
	});

	onUnmounted(() => {
		document.body.style.overflow = '';
	});
</script>

<style scoped src="@/assets/css/loans/form.css"></style>
<style scoped src="@/assets/css/loans/repull-modal.css"></style>